import React, { useEffect, useRef, useState } from 'react';
import { ArrowRight, Bot, Brain, Check, ChevronDown, MessagesSquare, ShieldCheck, Sparkles, X, Zap } from 'lucide-react';
import { AnimatePresence, motion, useReducedMotion, useScroll, useSpring, useTransform } from 'framer-motion';
import { Link } from 'react-router-dom';
import { SlackPanel } from '../components/SlackIntegrationGraph';
import { CodeTerminal, TabIcon } from './CompanyBrain';
import { ChatGPTPanel } from '../components/alt/ChatGPTPanel';
import { HeroCameraScene } from '../components/alt/HeroCameraScene';
import { ConnectedAppsPoints, ConnectionsScreen } from '../components/alt/ConnectedAppsPillar';
import { ConnectStage } from '../components/alt/ConnectStage';
import { ProductVignette } from '../components/alt/ProductVignette';
import { AccelerationMosaic } from '../components/alt/AccelerationMosaic';
import { SecurityEnterprise } from '../components/alt/SecurityEnterprise';
import { PlatformPillar } from '../components/alt/PlatformPillar';
import { BrainFlowExperiment } from '../components/alt/BrainFlowExperiment';
import { ArchitectureWidget } from '../components/alt/ArchitectureWidget';
import { PillTabs } from '../components/alt/PillTabs';
import { AppChrome, BrandLogo } from '../components/alt/AppChrome';
import { SectionIntro } from '../components/alt/SectionIntro';
import { TALK_TO_SALES_URL, SUPPORT_EMAIL, SUPPORT_MAILTO } from '../lib/links';

// Order carries the argument: connect the tools, let what they know compound,
// take that intelligence to where people already work, and build on it.
// Integrations is deliberately NOT a chapter here. The bar exists to let a
// reader jump between the parts of the argument they care about, and the
// Integrations section is the one they are already standing in when the bar
// appears — an anchor to where you are is a dead control. It also keeps the
// bar to three items, which fits on a phone without scrolling. The section
// keeps its id, so /#connected-apps still works from the nav and elsewhere.
const SECTIONS = [{
  id: 'company-brain',
  label: 'Company Brain',
  icon: Brain
}, {
  id: 'co-worker',
  label: 'AI Co Worker',
  icon: Bot
}, {
  id: 'workspace',
  label: 'Workspace',
  icon: MessagesSquare
}] as const;
type SectionId = (typeof SECTIONS)[number]['id'];

/**
 * Surfaces the co worker runs in. Slack and Teams are listed separately rather
 * than collapsed into one "chat" tab: a Teams shop should see Teams named on
 * the page, not discover it behind a toggle inside a Slack screenshot.
 */
type SurfaceKey = 'slack' | 'teams' | 'code' | 'chatgpt';
export function Alt() {
  const [activeSection, setActiveSection] = useState<SectionId>('company-brain');
  const [activeSurface, setActiveSurface] = useState<SurfaceKey>('slack');
  // The task field behind the surface panel needs the panel's right edge, so a
  // request's completion mark fires the instant the card clears it.
  const surfacePanelRef = React.useRef<HTMLDivElement>(null);
  // Desk-plate parallax. The plate is drawn 20% taller than the section at each
  // end and travels the opposite way by the same amount, so its motion cancels
  // most of the page's as the section crosses the viewport — it reads as a
  // surface being travelled over rather than an image glued to the column.
  const deskSectionRef = useRef<HTMLElement | null>(null);
  const reduceDeskMotion = useReducedMotion();
  const {
    scrollYProgress: deskProgress
  } = useScroll({
    target: deskSectionRef,
    offset: ['start end', 'end start']
  });
  const deskParallax = useTransform(deskProgress, [0, 1], reduceDeskMotion ? ['0%', '0%'] : ['-14%', '14%']);
  // The pillar bar is pinned for the whole page, so it crosses both the
  // photographic scenes and the cream sections. A cream bar cutting across the
  // plate was the one piece of page chrome still sitting on top of the image;
  // over a scene it becomes dark glass instead.
  const [overPlate, setOverPlate] = useState(true);
  // The bar is pinned for the rest of the page, but it stays out of the way
  // while the opening statement holds the frame.
  const [navVisible, setNavVisible] = useState(false);
  const navRef = useRef<HTMLDivElement | null>(null);
  // An IntersectionObserver band was mis-firing here: these sections are much
  // taller than the band, so with direction-dependent tie-breaking the active
  // tab could jump a section early or lag a section behind. A single reference
  // line just below the sticky nav is unambiguous — the active section is
  // simply the last one whose top has passed it.
  useEffect(() => {
    const update = () => {
      // Measured from the element's own height rather than its rect: the bar
      // is translated off-screen while hidden, and a transformed rect would
      // drag the reference line up with it.
      const navHeight = navRef.current?.offsetHeight ?? 56;
      const line = navHeight + 24;
      let current: SectionId = SECTIONS[0].id;
      for (const section of SECTIONS) {
        const el = document.getElementById(section.id);
        if (el && el.getBoundingClientRect().top <= line) current = section.id;
      }
      setActiveSection(current);
      // What is actually behind the bar right now, not which section is
      // "active": the bar has to match the surface it is drawn over.
      const scenes = Array.from(document.querySelectorAll<HTMLElement>('[data-camera-scene]'));
      const dark = Array.from(document.querySelectorAll<HTMLElement>('[data-camera-scene],[data-dark-section]'));
      const onPlate = dark.some(el => {
        const r = el.getBoundingClientRect();
        return r.top < navHeight && r.bottom > 0;
      });
      setOverPlate(onPlate);
      // The bar belongs to the page's chapters, and the opening scene is not
      // one of them — it is the page introducing itself.
      const first = scenes[0];
      if (!first) {
        setNavVisible(window.scrollY > 40);
        return;
      }
      // Only once the chapters actually begin. Over the opening scene the bar
      // was labelling a section the reader had not reached yet.
      setNavVisible(first.getBoundingClientRect().bottom <= navHeight + 1);
    };
    update();
    window.addEventListener('scroll', update, {
      passive: true
    });
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, []);
  const scrollToSection = (sectionId: SectionId) => {
    const el = document.getElementById(sectionId);
    if (!el) return;
    // The pillar bar is the ONLY thing pinned to the top of the viewport on
    // this page — the site header scrolls away — so the section only needs to
    // clear the bar's own height, plus a little breathing room.
    // Align the section's FIRST piece of content (its eyebrow) rather than the
    // section box — the box carries 80–112px of its own top padding, which
    // otherwise reads as dead space under the bar. Falls back to the box.
    // A camera scene is the exception: its copy lives inside a pinned viewport,
    // so the copy's position tells you nothing about where the scene starts.
    // Land on the scene box, which is progress 0 — the opening frame.
    const navHeight = navRef.current?.offsetHeight ?? 0;
    const isScene = el.hasAttribute('data-camera-scene');
    const anchor = isScene ? el : el.querySelector('p, h2, h3') ?? el;
    const top = anchor.getBoundingClientRect().top + window.scrollY - (isScene ? navHeight : navHeight + 18);
    window.scrollTo({
      top,
      behavior: 'smooth'
    });
  };
  return <div className="giga-home relative min-h-screen bg-[#FAF8F4] text-[#2B211A]" data-id="element-6820" data-mp="2rh7m9g5u691s">
      {/* The opening statement and the product are two framings of one shot, so
       the page starts flush to the top and the site header — which is
       transparent and scrolls away on this route — floats over it. */}
      <HeroCameraScene data-id="element-6821" data-mp="209xzxbfdtlrl" />

      {/* Once the opening scene is behind you this becomes the page's header:
       mark on the left, chapters centred, the action on the right. The site
       header has scrolled away by then, so without the mark and the CTA the
       reader would be deep in the page with no way out of it. Glass rather
       than a solid bar — it sits over both the cream page and the photograph,
       and a blurred, saturated surface reads as the page continuing beneath
       it rather than as a panel laid on top. */}
      {/* PHONES DON'T GET IT. It occupies the same top strip as the site
       header, so on a phone it covered the hamburger and the mark — trading
       the page's actual navigation for a chapter list. On a narrow screen the
       reader is scrolling one column anyway and does not need a chapter index;
       they need the menu. From sm up there is room for both. */}
      <div ref={navRef} className={`fixed inset-x-0 top-0 z-40 hidden border-b [backdrop-filter:blur(20px)_saturate(180%)] transition-[background-color,border-color,transform,opacity] duration-200 ease-out sm:block ${navVisible ? 'translate-y-0 opacity-100' : 'pointer-events-none -translate-y-full opacity-0'} ${overPlate ? 'border-white/10 bg-[#1A120C]/60 shadow-[inset_0_1px_0_rgba(255,255,255,0.12)]' : 'border-[#E7E0D4]/80 bg-[#FAF8F4]/70 shadow-[inset_0_1px_0_rgba(255,255,255,0.9)]'}`} data-id="element-6822" data-mp="13ix7cn08mqf5">
        <nav className="container mx-auto grid grid-cols-[auto_1fr] items-center gap-3 px-4 py-2.5 sm:grid-cols-[auto_1fr_auto] sm:gap-4 sm:px-6 sm:py-3" aria-label="Section navigation" data-id="element-6823" data-mp="3noo0v00vnwr">
          <Link to="/" aria-label="Giga home" className="group flex shrink-0 items-center gap-2" data-id="element-6824" data-mp="3qrmenpfcc2kz">
            <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="" aria-hidden="true" className={`h-6 w-6 object-contain transition-transform group-hover:scale-105 ${overPlate ? '[filter:brightness(0)_invert(1)]' : ''}`} data-id="element-6825" data-mp="3lt1oyjxpt1as" />
            <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/giga4x.png" alt="Giga" className={`hidden h-5 object-contain transition-[filter] sm:block ${overPlate ? '[filter:brightness(0)_invert(1)]' : ''}`} data-id="element-6826" data-mp="20vqe5k5aydii" />
          </Link>

          {/* The chapter list scrolls rather than wraps on a phone, so it needs
           an edge fade — without one there is nothing to say more chapters
           exist off the right edge. Left-anchored below sm for the same
           reason: centring hides the first chapter as often as the last. */}
          {/* The fade is on the RIGHT ONLY, and the row starts hard at its
           left edge. A fade on both sides washed out the first chapter's
           initial — so "Integrations" read as clipped rather than as the start
           of a list — while the thing that actually needs signalling is that
           there is more off the right. A longer right ramp makes that read as
           a fade instead of a crop. */}
          <div className="hide-scrollbar flex items-center justify-start gap-4 overflow-x-auto pr-1 [mask-image:linear-gradient(to_right,#000_0,#000_calc(100%-34px),transparent)] sm:justify-center sm:gap-8 sm:pr-0 sm:[mask-image:none]" data-id="element-6827" data-mp="3asospa88434d">
            {SECTIONS.map(section => {
            const isActive = activeSection === section.id;
            const state = isActive ? overPlate ? 'text-white' : 'text-[#2B211A]' : overPlate ? 'text-white/55 hover:text-white' : 'text-[#8C7C6E] hover:text-[#2B211A]';
            return <button key={section.id} type="button" onClick={() => scrollToSection(section.id)} className={`group relative shrink-0 whitespace-nowrap py-1 text-[12.5px] font-semibold transition-colors duration-200 ease-out sm:text-[13.5px] ${state}`} aria-current={isActive ? 'true' : undefined} data-id="element-6828" data-mp="2d567v11afak0">
                  {section.label}
                  {/* The active chapter is underscored rather than filled — a
                   mark on the same hairline the bar already sits on. */}
                  <span aria-hidden="true" className={`absolute inset-x-0 -bottom-[11px] h-[2px] origin-left rounded-full bg-[#EA580C] transition-transform duration-200 ease-out sm:-bottom-[13px] ${isActive ? 'scale-x-100' : 'scale-x-0'}`} data-id="element-6829" data-mp="9wdzvey2z928" />
                </button>;
          })}
          </div>

          <Link to="/signup" className={`hidden shrink-0 rounded-full px-4 py-2 text-[13.5px] font-bold transition-colors duration-200 ease-out sm:inline-flex ${overPlate ? 'bg-white text-[#2B211A] hover:bg-white/90' : 'bg-[#EA580C] text-white hover:bg-[#C2410C]'}`} data-id="element-6830" data-mp="kse9z7y5rb">
            Get started
          </Link>
        </nav>
      </div>

      <main data-id="element-6831" data-mp="ixubtr4jnj8k">
        {/* FIRST after the opening scene. The hero states the promise — go
         faster, stay in control — so the board that unpacks it belongs
         immediately underneath, before the page starts explaining mechanism.
         Everything below is HOW; this is WHAT. */}
        <section className="bg-[#FAF8F4] py-20 md:py-32" data-id="element-6832" data-mp="1juvv2wqklbqe">
          <div className="mx-auto max-w-[1280px] px-6 md:px-10" data-id="element-6833" data-mp="1jwgt4wc3fct0">
            <AccelerationMosaic data-id="element-6834" data-mp="1nwqws2cexr4p" />
          </div>
        </section>

        {/* ONE CHAPTER, ONE GROUND. The claim, the glass panel, the four points
         and the comparison are a single argument, so they share a single dark
         ground — splitting them across two grounds read as two topics. Glass
         also only reads as glass over something dark. */}
        <section id="connected-apps" data-dark-section="" className="scroll-mt-24 bg-[#1A120C]" data-id="element-6835" data-mp="26uler5vircz8">
          <div className="pb-16 pt-20 md:pb-24 md:pt-32" data-id="element-6836" data-mp="2bltoy44ob2dg">
            <div className="mx-auto max-w-[1000px] px-6 md:px-10" data-id="element-6837" data-mp="2bmbr1he0oata">
              {/* Centred: the screen below is a symmetrical object spanning the
               full column, so a left-aligned heading over it left the block
               visibly off its own axis. */}
              {/* LEFT-ALIGNED ON PHONES. Centred, both sentences wrap mid-phrase
               and every line starts from a different x — "Giga once." ends up
               alone on a line and the lead becomes a diamond with one orphaned
               word at its point. A single left margin gives the reader one
               entry point per line, which is what makes a wrapping block
               readable. Above sm the lines fit as written and centring earns
               its keep against the symmetrical screen below. */}
              <h2 className="font-title text-[32px] font-bold leading-[1.08] tracking-tight text-[#FAF8F4] sm:text-center sm:text-4xl md:text-[56px] md:leading-[1.14]" data-id="element-6838" data-mp="34dfxom5w4uih">
                Connect your tools to Giga once.
                <br className="hidden sm:block" data-id="element-6839" data-mp="39ag2ktr07r80" />{' '}
                Use them everywhere<span className="text-[#EA580C]" data-id="element-6840" data-mp="39950cyd7irj0">.</span>
              </h2>
              <p className="mt-4 max-w-[54ch] text-[16.5px] leading-relaxed text-[#C9BFB4] sm:mx-auto sm:text-center md:mt-5 md:text-lg" data-id="element-6841" data-mp="skhnh9xt1vvz">
                4,000 tools in the directory, plus databases, API keys, and
                email-and-password logins for the ones without an API.
              </p>
            </div>

            {/* The reader performs the claim. Nothing but one oversized
             Connect control and a pointer until they click it — then the
             directory and the screen it produces unfold into the space. */}
            {/* The Connections screen is the section's evidence, so it is
             presented as a lit window rather than a pasted screenshot: it runs
             wider than the reading column and its edges fall away into the
             section's own ground. See ProductVignette. */}
            <div className="mx-auto mt-8 max-w-[1000px] px-6 md:mt-10 md:px-10" data-id="element-6842" data-mp="152kcp292lxod">
              <ConnectStage data-id="element-6843" data-mp="1a1olvsvzvvep">
                <ProductVignette ground="#1A120C" focusY={30} data-id="element-6844" data-mp="1xt2f011yzv0q">
                  <ConnectionsReveal data-id="element-6845" data-mp="1xritnw1gits3" />
                </ProductVignette>
              </ConnectStage>
            </div>
          </div>

          <div className="mx-auto max-w-[1000px] px-6 pb-20 md:px-10 md:pb-32" data-id="element-6846" data-mp="2cfsnpni0wpwc">
            <div data-id="element-6847" data-mp="2cdold9zrvyy6">
              <ConnectedAppsPoints dark data-id="element-6848" data-mp="27gofp06u4y7l" />
            </div>
            {/* The comparison is the answer to a question only some readers
             are asking, so it waits behind a control instead of adding ten
             dense rows to a section that has already made its case. */}
            <ComparisonDisclosure dark data-id="element-6849" data-mp="1yicgwavteu42" />
          </div>
        </section>

        {/* A dark stage rather than another cream panel. The brain is the one
         section that is always in motion — observations arriving, counts
         ticking — and on cream that reads as noise; on dark ground the moving
         parts are the only lit things on screen. The faint dot field is the
         page's own texture, not an image, so nothing has to load. */}
        {/* A hairline, not a change of ground: Integrations and the brain are
         two chapters of the same dark act, so the boundary should mark a new
         chapter without breaking the act in half. */}
        <section id="company-brain" data-dark-section="" className="relative scroll-mt-24 overflow-hidden border-t border-white/[0.08] bg-[#1A120C] py-20 md:py-32" data-id="element-6850" data-mp="3hdwdzw47jyqp">
          {/* A fine dot field on the top and bottom edges only, faded out
           before it reaches the content. Carried across the whole panel it
           sat behind the diagram and the type and read as dirt on the
           screen; held to the margins it just gives the dark ground a
           surface. */}
          <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-0 h-40 opacity-[0.09] [mask-image:linear-gradient(to_bottom,#000,transparent)]" style={{
          backgroundImage: 'radial-gradient(rgba(255,255,255,0.9) 0.5px, transparent 0.5px)',
          backgroundSize: '14px 14px'
        }} data-id="element-6851" data-mp="33ocer09u5jm5" />
          <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 bottom-0 h-40 opacity-[0.09] [mask-image:linear-gradient(to_top,#000,transparent)]" style={{
          backgroundImage: 'radial-gradient(rgba(255,255,255,0.9) 0.5px, transparent 0.5px)',
          backgroundSize: '14px 14px'
        }} data-id="element-6852" data-mp="2fx6xqhs5sla9" />
          <div className="relative mx-auto max-w-[1280px] px-6 md:px-10" data-id="element-6853" data-mp="18ezbglqroi74">
            <BrainFlowExperiment data-id="element-6854" data-mp="1nbqf577h4g2j" />
          </div>
        </section>

        {/* Desk-plate ground: spreadsheet scrap, calendar, punched notes and
         register marks scattered around a clear centre. It carries ALL of its
         artwork at the far left and right, so it frames the centred column
         rather than sitting behind it — the work the teammate is being asked
         about, laid out around the surface it is asked through.
          STRETCHED, not `cover`. The plate is a wide 16:9 and this section is
         much taller, so `cover` would scale it to the height and throw away
         the outer thirds — which is exactly where the artwork lives, leaving
         the layer effectively invisible. Filling both axes keeps the cut
         edges, punch holes and crosshairs hard against the section's own
         edges; on a scattered-paper plate the vertical stretch reads as
         nothing at all. The section's own fill is the plate's cream so the
         seam at the edges is invisible. */}
        <section ref={deskSectionRef} id="co-worker" className="relative scroll-mt-24 overflow-hidden border-b border-[#E7E0D4] bg-[#F5F2EA] py-20 md:py-32" data-id="element-6855" data-mp="2kpg9plzq64n7">
          {/* PARALLAX: the plate holds while the column travels over it.
           *
           * It is drawn OVERSIZE (top/bottom -20%) and given a counter-travel
           * of the same magnitude, so as the section crosses the viewport the
           * plate's own movement almost cancels the page's — the desk reads as
           * a surface the content is being carried across, not a picture glued
           * to it. Oversizing is what makes that possible without exposing a
           * bare strip at either end.
           *
           * Scroll-linked, so it is driven by the reader rather than playing on
           * its own — but under reduced motion it is pinned flat, since the
           * effect is decorative depth and nothing depends on it. */}
          {/* A PORTRAIT PLATE ON PHONES, the wide one from md up. Stretching a
           16:9 plate down a tall narrow section pulled its artefacts into
           streaks — the tables and charts live at the left and right edges,
           which is exactly what a portrait crop has to spare. Two plates, one
           per orientation, rather than one plate distorted to fit both. */}
          <motion.div aria-hidden="true" className="pointer-events-none absolute inset-x-0 -top-[20%] -bottom-[20%] bg-center bg-no-repeat will-change-transform md:hidden" style={{
          backgroundImage: 'url(https://cdn.magicpatterns.com/uploads/vtCDqwp92R6XYPecrS1pPb/hf_20260904_114854_5c710d02-9a8b-4448-b2f5-fe454dd0cb1c.png)',
          backgroundSize: '100% 100%',
          y: deskParallax
        }} data-id="element-6856" data-mp="2jtrjigbuum6x" />
          <motion.div aria-hidden="true" className="pointer-events-none absolute inset-x-0 -top-[20%] -bottom-[20%] hidden bg-center bg-no-repeat will-change-transform md:block" style={{
          backgroundImage: 'url(https://cdn.magicpatterns.com/uploads/uAyQWg7CUqoTPvQT35zWX1/hf_20260904_114600_6a9f16c4-0057-4b15-a901-e93a76b45225.png)',
          backgroundSize: '100% 100%',
          y: deskParallax
        }} data-id="element-6857" data-mp="r37f289205ij" />
          {/* A CREAM VEIL, NOT A BROWN ONE. Cream is the page's own paper, so
           it settles the plate back a step without changing its temperature —
           a brown wash at this strength would tint the whole section warmer
           than the sections either side of it and read as a different
           material. It sits ABOVE both plates and BELOW the content, and it
           does not move with the parallax: a veil that travels with the image
           reads as part of the image, where a held one reads as the page's
           surface sitting over it.
            Stronger at the centre than at the edges, because that is where the
           frame and the copy land — the artefacts at the far edges are what
           make the plate legible as a desk, so they keep more of their
           contrast. */}
          <div aria-hidden="true" className="pointer-events-none absolute inset-0" style={{
          background: 'radial-gradient(110% 80% at 50% 45%, rgba(245,242,234,0.82) 0%, rgba(245,242,234,0.66) 55%, rgba(245,242,234,0.42) 100%)'
        }} data-id="element-6858" data-mp="u95ch5tdv7md" />
          <div className="relative mx-auto max-w-[1280px] px-6 md:px-10" data-id="element-6859" data-mp="6irrfz97u91a">
            <SectionIntro className="mx-auto mb-10 max-w-3xl text-center md:mb-16" data-id="element-6860" data-mp="6gosuqtzz7ky">
              <h2 className="font-title text-[32px] font-bold leading-[1.08] tracking-tight text-[#2B211A] sm:text-4xl md:text-[56px] md:leading-[1.14]" data-id="element-6861" data-mp="bgtc2omaigev">
                An AI teammate
                <br data-id="element-6862" data-mp="3eybhyqii5dcn" />
                inside your existing tools<span className="text-[#EA580C]" data-id="element-6863" data-mp="3jxo396tq6cd4">.</span>
              </h2>
              <p className="mx-auto mt-4 max-w-[54ch] text-[16.5px] leading-relaxed text-[#6B5D52] md:mt-5 md:text-lg" data-id="element-6864" data-mp="3jvuyjx2oa27r">
                Summon Giga from Slack, ChatGPT, Claude Code or Cowork, and talk to it like you would a normal colleague. 
              </p>
            </SectionIntro>

            {/* One centred column: the surface switcher sits directly above the
             frame it controls, so the relationship needs no explaining.
              The task field is layered BEHIND that column, bleeding past the
             page's gutters. It gives the frame something to sit in — the work
             being asked for, drifting behind the surface it is asked through —
             where a strip beneath the panel read as a second thing to consider.
             Hidden below md: at phone width the panel covers the full column,
             so the field would only ever show as slivers at the edges. */}
            {/* The switcher sits OUTSIDE the field's container, so no task
             cards run behind it: the tabs are a control, and a control needs a
             clean ground to read as clickable rather than as one more card in
             the drift. The field begins at the top edge of the frame it sits
             behind. */}
            <SurfaceTabsControl activeSurface={activeSurface} setActiveSurface={setActiveSurface} className="mb-8 hidden justify-center md:mb-10 md:flex" data-id="element-6865" data-mp="3w1d584uq08zi" />
            <div className="relative" data-id="element-6866" data-mp="4tsbjz1mms8x">
              {/* Inset from the frame's top and bottom edges rather than flush
               with them: a row landing exactly on the panel's edge made the two
               read as one aligned object, which is the opposite of the intended
               effect — the field should look like it carries on behind the
               panel, so its rows have to sit clear of its boundaries. */}
              {/* Task field hidden for now — the desk plate behind this
               section already gives the frame something to sit in, and the
               drifting rows were crossing its cut edges and punch holes. */}
              {/* The field measures this element's right edge so a request's
               completion mark fires exactly where the card re-emerges from
               behind the panel. */}
              <div ref={surfacePanelRef} className="relative mx-auto max-w-[820px]" data-id="element-6867" data-mp="1z8bzw43hhf53">
                <SurfacePreview activeSurface={activeSurface} setActiveSurface={setActiveSurface} data-id="element-6868" data-mp="1ugk9puo8pbfh" />
              </div>
            </div>

            {/* AFTER the demonstration, not beside the headline: the reader
             only wants more on the co-worker once they have watched it work.
             Deliberately a quiet outlined control rather than a filled one —
             the page has exactly one primary action, and it is the closing
             CTA. A solid button here would compete with it. */}
            <div className="mt-10 flex justify-center md:mt-14" data-id="element-6869" data-mp="nt54vx5v086e">
              {/* ONE LINE, ALWAYS. Wrapped, the label turned the pill into a
               tall lozenge with the arrow stranded in dead space beside two
               ragged lines — a pill only reads as a control at one line high.
               The phone label drops "Learn about", which the arrow already
               says. */}
              {/* SOLID BROWN, NOT AN OUTLINE. This sits at the foot of a
               section whose ground fades to near-white, and a cream pill with
               a hairline border on almost-white ground reads as a disabled
               control rather than a quiet one. Ink on light is the same
               relationship the page's other filled controls have with their
               own ground. */}
              <Link to="/ai-coworker" className="group inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-[#2B211A] px-5 py-3 text-[14px] font-bold text-[#FAF8F4] shadow-[0_10px_24px_-14px_rgba(43,33,26,0.55)] transition-[background-color,transform] duration-200 ease-out hover:-translate-y-px hover:bg-[#1A120C] sm:px-7 sm:py-3.5 sm:text-[15px]" data-id="element-6870" data-mp="3bkplhiwtszbe">
                {/* One space, from one place. The hidden prefix carried its own
                 &nbsp; AND picked up JSX's newline space, so the two words sat
                 a double space apart. */}
                <span className="hidden sm:inline" data-id="element-6871" data-mp="14v5f51hi478w">Learn about </span>
                <span data-id="element-6872" data-mp="zwu5orv4byr7">Giga as an AI co-worker</span>
                <ArrowRight size={17} className="text-[#FB923C] transition-transform duration-200 ease-out group-hover:translate-x-0.5" data-id="element-6873" data-mp="zy5jlkia2mw1" />
              </Link>
            </div>
          </div>
        </section>

        {/* The page's own column — the same 1280px measure and gutter as
         Integrations and the brain. On the site header's wider container this
         section was the only one on the page running past that measure, so it
         read as stretched rather than as generous. */}
        <section id="workspace" className="scroll-mt-24 bg-white py-20 md:py-32" data-id="element-6874" data-mp="2gbvr718lvtaf">
          <div className="mx-auto max-w-[1280px] px-6 md:px-10" data-id="element-6875" data-mp="2bk3vydei8zdy">
            <PlatformPillar data-id="element-6876" data-mp="1jwssexrjepw8" />
          </div>
        </section>

        {/* White canvas: the diagram's group panels are cream, so they need a
         lighter ground now that the enclosing card is gone. */}
        <section className="border-t border-[#E7E0D4] bg-white py-20 md:py-32" data-id="element-6877" data-mp="1awe5xv2u74r5">
          <div className="mx-auto max-w-[1280px] px-6 md:px-10" data-id="element-6878" data-mp="1aub50g05br7n">
            <ArchitectureWidget data-id="element-6879" data-mp="1eyiuifefhb6x" />
          </div>
        </section>

        {/* The IT-approval beat, on dark ground: the page changes audience
         here — from the team that wants Giga to the person who has to approve
         it — so the ground changes with it. Last objection before the ask. */}
        <section className="bg-[#1A120C] py-20 md:py-32" data-id="element-6880" data-mp="1tm3yebj4nefw">
          <SecurityEnterprise data-id="element-6881" data-mp="1yl7500er9spz" />
        </section>

        {/* The closing figure overflows the CTA panel's bottom edge, so this
         section's bottom padding is set to EXACTLY that overflow — the
         co-worker's feet then land on the footer's top edge instead of floating
         in a band of leftover cream. */}
        {/* NO TOP PADDING. The CTA panel already carries a large top margin to
         clear the co-worker breaking past its top edge, so a section pad on
         top of that stacked into ~320px of empty cream after the dark IT
         band — read as a gap where something failed to render. The panel's
         own margin is the only clearance needed. */}
        <section className="bg-[#FAF8F4] pb-20 md:pb-32" data-id="element-6882" data-mp="3s4fjzo0jcjz6">
          <div className="mx-auto max-w-[1280px] px-6 md:px-10" data-id="element-6883" data-mp="3s4yyeuqz8q8g">
            <ClosingCTA data-id="element-6884" data-mp="3n649rc6bw7gf" />
          </div>
        </section>
      </main>
    </div>;
}
function SurfacePreview({
  activeSurface,
  setActiveSurface
}: {
  activeSurface: SurfaceKey;
  setActiveSurface: (surface: SurfaceKey) => void;
}) {
  const mobileTabs = <SurfaceTabsControl activeSurface={activeSurface} setActiveSurface={setActiveSurface} className="relative z-10 flex md:hidden" tabClassName="!flex-1 !justify-center !px-2 !py-2 !text-xs" quiet data-id="element-6885" data-mp="w1o8ujfh1ws" />;
  // ONE FRAME, four contents. Every surface now renders at the same width and
  // the same height, so switching tabs changes what is inside the window and
  // nothing else — before, Slack was 40px narrower and the terminal a good
  // 150px shorter, so the page jumped and reflowed on every switch. The shadow
  // matters now that the task field drifts behind: without it the panel sits
  // flat among the cards instead of over them.
  const shell = 'relative mx-auto w-full max-w-[760px] md:shadow-[0_24px_60px_-28px_rgba(43,33,26,0.35)]';
  const height = 'h-[520px] sm:h-[600px]';
  if (activeSurface === 'chatgpt') {
    return <div className={shell} data-id="element-6886" data-mp="2bezhz1pf3wvx">
        {mobileTabs}
        <div className="relative [&>div]:rounded-tl-none md:[&>div]:rounded-tl-[20px]" data-id="element-6887" data-mp="2bdph64r6kall">
          <ChatGPTPanel heightClass={height} data-id="element-6888" data-mp="27ddwwt2qccan" />
        </div>
      </div>;
  }
  if (activeSurface === 'code') {
    // The terminal sizes to its own content, so it is stretched to the shared
    // height here — and its built-in orange halo is suppressed, since the other
    // three frames no longer carry one.
    /*
     * THE TERMINAL'S OVERRIDES ARE SCOPED TO THE TERMINAL.
     *
     * They used to sit on the shell and target its FIRST div child — which was
     * meant to be the terminal's own orange halo, but on a phone the tab row
     * is rendered first, so `:first-of-type` matched the tabs and hid them.
     * Selecting the tabs away was a side effect of a selector aimed at
     * something else. Wrapping the terminal gives those rules their own scope.
     */
    return <div className={`${shell} ${height} flex flex-col`} data-id="element-6889" data-mp="3van0rhrbb8fk">
        {mobileTabs}
        <div className="min-h-0 flex-1 [&>div:first-of-type]:hidden [&>div:last-of-type>div]:flex [&>div:last-of-type>div]:h-full [&>div:last-of-type>div]:flex-col [&>div:last-of-type]:h-full [&_.min-h-\\[390px\\]]:min-h-0 [&_.min-h-\\[390px\\]]:flex-1" data-id="element-6890" data-mp="37nlp8k5a7im">
          <CodeTerminal data-id="element-6891" data-mp="251d1jqtgdpi8" />
        </div>
      </div>;
  }
  // Slack and Teams are separate tabs now, so the panel's own platform toggle
  // is suppressed — the tab row above it already made that choice.
  return <div className={shell} data-id="element-6892" data-mp="3db3ulull64jy">
      {mobileTabs}
      <div className="relative text-left [&>div]:rounded-tl-none md:[&>div]:rounded-tl-2xl" data-id="element-6893" data-mp="38b7sk0wg8n71">
        <SlackPanel className="!max-w-none w-full" heightClass={height} platform={activeSurface === 'teams' ? 'teams' : 'slack'} showPlatformToggle={false} data-id="element-6894" data-mp="3ma7cwwcxifxe" />
      </div>
    </div>;
}
function SurfaceTabsControl({
  activeSurface,
  setActiveSurface,
  className = '',
  tabClassName = '',
  quiet = false
}: {
  activeSurface: SurfaceKey;
  setActiveSurface: (surface: SurfaceKey) => void;
  className?: string;
  tabClassName?: string;
  quiet?: boolean;
}) {
  if (quiet) {
    const tabs = [{
      surface: 'slack' as const,
      label: 'Slack',
      icon: <TabIcon src="https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/slack.svg" fallback="S" data-id="element-6895" data-mp="4vto8aanyd6d" />
    }, {
      surface: 'teams' as const,
      label: 'Teams',
      icon: <TabIcon src="https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/microsoft-teams.svg" fallback="T" data-id="element-6896" data-mp="dvytoewar1hw" />,
      ariaLabel: 'Microsoft Teams'
    }, {
      surface: 'code' as const,
      label: 'Claude',
      icon: <TabIcon slug="claude" fallback="C" data-id="element-6897" data-mp="12co5b2k8zk6i" />,
      ariaLabel: 'Claude'
    }, {
      surface: 'chatgpt' as const,
      label: 'ChatGPT',
      icon: <TabIcon src="https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/openai.svg" fallback="AI" data-id="element-6898" data-mp="sg1hq60jef0s" />
    }];
    return <div className={`w-full items-center gap-0 ${className}`} role="tablist" aria-label="Chat surface" data-id="element-6899" data-mp="eoo5ze8velgb">
        {tabs.map(tab => {
        const active = activeSurface === tab.surface;
        return <button key={tab.surface} type="button" role="tab" aria-selected={active} aria-label={tab.ariaLabel} onClick={() => setActiveSurface(tab.surface)} className={`inline-flex items-center gap-1.5 rounded-t-[16px] border border-transparent px-2 py-2 text-xs text-[#2B211A] transition-colors ${active ? 'bg-white font-bold shadow-sm' : 'font-semibold text-[#6B5D52]'} ${tabClassName}`} data-id="element-6900" data-mp="4sl0pziq26p5">
              {tab.icon}
              {tab.label}
            </button>;
      })}
      </div>;
  }
  // Same control as the architecture diagram's preset tabs, with each
  // platform's logo added so the surfaces are identifiable at a glance.
  return <div className={`w-full items-center ${className}`} data-id="element-6901" data-mp="3f2767pgx7e1i">
      <PillTabs ariaLabel="Chat surface" equal value={activeSurface} onChange={id => setActiveSurface(id as SurfaceKey)} className={tabClassName} items={[{
      id: 'slack',
      label: 'Slack',
      logo: 'https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/slack.svg'
    }, {
      id: 'teams',
      label: 'Teams',
      ariaLabel: 'Microsoft Teams',
      logo: 'https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/microsoft-teams.svg'
    }, {
      id: 'code',
      label: 'Claude',
      ariaLabel: 'Claude',
      logo: 'https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/claude-ai.svg'
    }, {
      id: 'chatgpt',
      label: 'ChatGPT',
      logo: 'https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/chatgpt.svg'
    }]} data-id="element-6902" data-mp="3a4fcqr0mnjv7" />
    </div>;
}
function ClosingCTA() {
  // The closing panel is the one dark surface on the page, so it carries the
  // brand halftone rather than reading as a flat black rectangle.
  // NOTE: overflow-hidden has moved off the panel and onto the texture layer.
  // The panel itself must not clip, so the co-worker can break past its top
  // edge; the halftone still needs clipping to keep the rounded corners.
  // The top margin is CLEARANCE FOR THE FIGURE, not section spacing — it is
  // sized to what the co-worker overflows above the panel's top edge, and the
  // enclosing section carries no top padding of its own.
  return <div className="relative mt-16 rounded-[24px] bg-[#1A120C] px-6 py-12 text-center shadow-[0_40px_100px_-45px_rgba(43,33,26,0.7)] sm:rounded-[28px] sm:px-8 sm:py-14 md:mt-20 md:px-16 md:py-20 lg:mt-40 lg:text-left" data-id="element-6903" data-mp="sy72m5m5qarh">
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden rounded-[28px]" data-id="element-6904" data-mp="24ardldfpf9r2">
        <div className="dot-matrix-dark absolute inset-0 opacity-40" data-id="element-6905" data-mp="1eq9r7dz3mdrh" />
      </div>
      {/* Anchored to the panel's bottom-right and deliberately overflowing the
       top edge, in front of the surface. Hidden below lg, where there is no
       column to spare beside the copy. */}
      {/* Sized by HEIGHT, not width: the composition is defined relative to the
       container's height — head clearly above the top edge, torso and laptop
       inside, legs only just crossing the bottom. Anchoring the feet near the
       bottom edge and making the figure taller than the panel puts every bit of
       the overflow at the TOP, which is where the break-out should read. */}
      <img src="https://cdn.magicpatterns.com/uploads/3CtotG8s5CuCGowVgFZ3tH/Untitled_design_(10).png" alt="" aria-hidden="true" className="pointer-events-none absolute bottom-[-18px] right-[-6px] z-10 hidden h-[540px] w-auto max-w-none select-none lg:block xl:bottom-[-22px] xl:right-[-14px] xl:h-[600px]" data-id="element-6906" data-mp="10v7d43k3li3l" />
      <div className="relative lg:pr-[430px] xl:pr-[540px]" data-id="element-6907" data-mp="2ju57wjq7m7rc">
        {/* A question, not a summary. "One platform to run your company's AI
         on" restated the whole page at the moment the reader has just finished
         reading it — asking whether they are bringing Giga in turns the panel
         into the next step instead of a recap. */}
        {/* Fluid on phones. At a flat 36px the question ran to four short
         ragged lines inside a 18ch measure — the panel's whole job is to ask
         one thing, and it was arriving as a paragraph. */}
        <h2 className="mx-auto max-w-[18ch] font-title text-[clamp(30px,8.4vw,40px)] font-bold leading-[1.1] tracking-[-0.025em] text-[#FAF8F4] sm:text-4xl md:text-[52px] md:leading-[1.14] lg:mx-0" data-id="element-6908" data-mp="2tm1b2975ko2f">
          Bringing Giga into your company<span className="text-[#EA580C]" data-id="element-6909" data-mp="1i4t3jf17mru1">?</span>
        </h2>
        <p className="mx-auto mt-4 max-w-[46ch] text-[16.5px] leading-relaxed text-[#C9BFB4] sm:mt-5 sm:text-lg lg:mx-0" data-id="element-6910" data-mp="1m7y3paxtihfi">
          Talk to us about rollout, security review, and the workflows your
          teams run every day.
        </p>
        {/* Matching the reference: ONE filled action, and the secondary as a
         plain arrow link rather than a second bordered pill. Two pills of equal
         weight made the reader choose between them; a single solid button with
         a text link beside it states which one is the next step. */}
        <div className="mt-8 flex flex-col items-center justify-center gap-x-8 gap-y-4 sm:mt-9 sm:flex-row lg:justify-start" data-id="element-6911" data-mp="2wnuzl0whhzd2">
          {/* FULL WIDTH ON PHONES. A pill sized to its label leaves a thumb
           aiming at a target floating in the middle of the panel; edge to
           edge it is unmissable and matches how every other primary action
           behaves on a phone. */}
          <a href={TALK_TO_SALES_URL} target="_blank" rel="noopener noreferrer" className="inline-flex w-full items-center justify-center rounded-full bg-white px-8 py-4 text-base font-bold text-[#2B211A] shadow-[0_14px_30px_-14px_rgba(0,0,0,0.6)] transition-[background-color,transform] duration-200 ease-out hover:-translate-y-px hover:bg-[#F5EFE7] sm:w-auto sm:py-3.5" data-id="element-6912" data-mp="3gb2hh8ou5mvw">
            Talk to sales
          </a>
          <a href="https://app.getgiga.com/signup" className="group inline-flex items-center gap-2 text-base font-bold text-white transition-colors duration-200 ease-out hover:text-white/75" data-id="element-6913" data-mp="3suq0goeld3l3">
            Get started free
            <ArrowRight size={17} className="transition-transform duration-200 ease-out group-hover:translate-x-0.5" data-id="element-6914" data-mp="amh17a152zi3" />
          </a>
        </div>
        <p className="mt-6 text-[13px] text-white/45 sm:mt-7 sm:text-[13.5px]" data-id="element-6915" data-mp="fes3ukn9kzec">
          $100 in credits included · no card needed
        </p>
        {/* A calendar asks for a name, a company, and thirty minutes of a
         future day before you can say a sentence. Anyone with a question
         rather than an evaluation needs a lower-commitment way through. */}
        <p className="mt-2.5 text-[13px] text-white/40 sm:text-[13.5px]" data-id="element-6916" data-mp="1yeogi98a89qv">
          Prefer email? Write to{' '}
          <a href={SUPPORT_MAILTO} className="font-medium text-white/70 underline decoration-white/25 underline-offset-2 transition-colors duration-200 ease-out hover:text-white" data-id="element-6917" data-mp="23by3li8789bj">
            {SUPPORT_EMAIL}
          </a>
        </p>

        {/* THE CO-WORKER APPEARS ON PHONES TOO, just seated instead of
         breaking out. Hidden below lg the panel had no figure at all, which
         is what made the mobile version read as the desktop one with its
         picture taken away. Standing on the panel's bottom edge — the
         negative margin cancels the panel's own padding — it closes the
         section with the same character, without needing a column beside the
         copy. */}
        <img src="https://cdn.magicpatterns.com/uploads/3CtotG8s5CuCGowVgFZ3tH/Untitled_design_(10).png" alt="" aria-hidden="true" className="pointer-events-none -mb-12 mt-8 h-[210px] w-auto max-w-none select-none self-center sm:-mb-14 sm:h-[250px] lg:hidden" style={{
        marginInline: 'auto'
      }} data-id="element-6918" data-mp="30rbyj38epnpl" />
      </div>
    </div>;
}
type ConnectedAppsValue = boolean | 'Enterprise only' | 'Coming soon' | null;
const CONNECTED_APPS_ROWS: Array<{
  feature: string;
  description: string;
  chatgpt: ConnectedAppsValue;
  anthropic: ConnectedAppsValue;
  giga: ConnectedAppsValue;
}> = [{
  feature: 'Supports MCP connections',
  description: 'Connect through existing MCP servers from supported providers.',
  chatgpt: true,
  anthropic: true,
  giga: true
}, {
  feature: 'Supports API connections',
  description: 'Stop having your team paste API tokens into conversations in plain text.',
  chatgpt: false,
  anthropic: false,
  giga: true
}, {
  feature: 'Supports SQL connections',
  description: 'See which databases your team connects to, review their permissions, and flag anything that looks dangerous.',
  chatgpt: false,
  anthropic: false,
  giga: true
}, {
  feature: 'Supports email/password connections',
  description: "Use Giga's cloud browser for tools without an API or MCP, so your agent can drive the browser and finish the work.",
  chatgpt: false,
  anthropic: false,
  giga: true
}, {
  feature: 'Supports unlisted tools',
  description: 'Use the on-demand integration wizard to add tools that are not yet in our directory.',
  chatgpt: false,
  anthropic: false,
  giga: true
}, {
  feature: 'Supports multiple accounts',
  description: 'Connect personal, team, client, and environment accounts without repeatedly signing in and out.',
  chatgpt: false,
  anthropic: false,
  giga: true
}, {
  feature: "Gives admins full visibility on the team's connections",
  description: 'Give admins one view of every connection, its owner, permissions, and recent use.',
  chatgpt: false,
  anthropic: false,
  giga: true
}, {
  feature: 'See what tools employees have connected',
  description: 'Understand which tools employees have authorized and where company data can flow.',
  chatgpt: 'Enterprise only',
  anthropic: 'Enterprise only',
  giga: true
}, {
  feature: 'Bring your own OAuth providers',
  description: 'Use your own OAuth app and consent screen when you need complete control over authorization.',
  chatgpt: null,
  anthropic: null,
  giga: true
}, {
  feature: 'Audit activity',
  description: 'Review how connections are used, by whom, and when, with detailed activity history.',
  chatgpt: null,
  anthropic: null,
  giga: 'Coming soon'
}];
/**
 * The comparison, one interaction away.
 *
 * Ten dense rows of feature-by-feature detail answer a question only some
 * readers are asking — and shown by default they were the heaviest thing in a
 * section that had already made its case. Behind a control the section ends on
 * its own argument, and the people who want the proof can pull it up.
 */
function ComparisonDisclosure({
  dark = false
}: {
  dark?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const reduce = useReducedMotion();
  return <div className="mt-16 flex flex-col items-center md:mt-20" data-id="element-6919" data-mp="bz5jxc3cw2fs">
      <button type="button" onClick={() => setOpen(value => !value)} aria-expanded={open} aria-controls="connected-apps-comparison" className={`group inline-flex items-center gap-2.5 rounded-full border px-5 py-3 text-[14.5px] font-bold transition-colors duration-200 ease-out ${dark ? 'border-white/15 bg-white/[0.07] text-white backdrop-blur-xl hover:border-white/25 hover:bg-white/[0.12]' : 'border-[#E7E0D4] bg-white text-[#2B211A] shadow-[0_1px_2px_rgba(43,33,26,0.05)] hover:border-[#D9CFBE] hover:bg-[#FFF8F3]'}`} data-id="element-6920" data-mp="gxgs7ie064vw">
        {/* Named opponents, in the shape people actually search for. "See how
         Giga compares" promised WORK — read a table — where "vs." promises a
         verdict, and competitor names are what make it worth clicking. */}
        {open ? 'Hide the comparison' : 'Giga vs. ChatGPT vs. Claude'}
        <ChevronDown size={16} strokeWidth={2.4} className={`transition-transform duration-200 ease-out ${dark ? 'text-[#FB923C]' : 'text-[#EA580C]'} ${open ? 'rotate-180' : ''}`} data-id="element-6921" data-mp="3bcppizyibiix" />
      </button>

      <AnimatePresence initial={false} data-id="element-6922" data-mp="2z3015i6j7q5e">
        {open && <motion.div id="connected-apps-comparison" initial={reduce ? false : {
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={reduce ? undefined : {
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.28,
        ease: [0.22, 1, 0.36, 1]
      }} className="w-full overflow-hidden" data-id="element-6923" data-mp="2z16s5w8g4tu8">
            <div className="pt-8" data-id="element-6924" data-mp="3huapbrkvhotl">
              {/* A heading, not a caption. What opens here is a section in its
               own right, so it should be introduced like one rather than by a
               line of body copy floating above a table. */}
              <h3 className={`font-title mx-auto max-w-[22ch] text-center text-2xl font-bold leading-[1.15] tracking-tight md:text-[32px] ${dark ? 'text-[#FAF8F4]' : 'text-[#2B211A]'}`} data-id="element-6925" data-mp="21g12j1wa484v">
                What you give up going direct.
              </h3>
              {/* The heading is deliberately short, so the lead has to name the
               alternative it is talking about — otherwise "direct" is doing
               work the reader has not been given. */}
              <p className={`mx-auto mb-7 mt-3.5 max-w-[58ch] text-center text-[15px] leading-relaxed ${dark ? 'text-[#C9BFB4]' : 'text-[#6B5D52]'}`} data-id="element-6926" data-mp="el5wdazgs6bm">
                You can connect a tool straight to ChatGPT or Claude today, and
                it will answer. What it won't do is remember what it found,
                share it with the rest of your team, or let you see what it
                touched.
              </p>
              <ConnectedAppsTable dark={dark} data-id="element-6927" data-mp="r53gxv71dj6" />
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
/**
 * The Connections screen arrives as the whole application — sidebar and all —
 * and then sets its own chrome aside as you scroll into it, leaving only the
 * surface the section is arguing about. The claim is "connect once, use
 * everywhere", so the frame should narrow to the connection layer itself.
 */
function ConnectionsReveal() {
  const ref = useRef<HTMLDivElement | null>(null);
  const reduce = useReducedMotion();
  const {
    scrollYProgress
  } = useScroll({
    target: ref,
    // Starts once the screen is properly in view and finishes well before it
    // leaves, so the collapse happens while the reader is looking at it.
    offset: ['start 0.75', 'start 0.3']
  });
  // Damped, so a flick does not snap the panel shut in a single frame.
  const smooth = useSpring(scrollYProgress, {
    stiffness: 110,
    damping: 26,
    mass: 0.4
  });
  const collapse = useTransform(smooth, [0, 1], [0, 1]);
  // The panel is glass from the first frame — it sits on the section's dark
  // ground, so there is nothing to transition INTO. What still moves is the
  // chrome: the sidebar sheds as you scroll in, leaving the connection map.
  return <div ref={ref} data-id="element-6928" data-mp="2lo9c9pqkl6x9">
      <AppChrome active="Connections" dark sidebarCollapse={reduce ? undefined : collapse} data-id="element-6929" data-mp="2lor5selul71h">
        <ConnectionsScreen dark data-id="element-6930" data-mp="2qn2h6trsjplt" />
      </AppChrome>
    </div>;
}
export function ConnectedAppsTable({
  dark = false
}: {
  /** Glass on dark ground, matching the Connections panel above it. */
  dark?: boolean;
} = {}) {
  return <div className={`mx-auto max-w-[900px] overflow-hidden rounded-[20px] border md:overflow-visible ${dark ? 'border-white/[0.14] bg-white/[0.055] shadow-[inset_0_1px_0_rgba(255,255,255,0.10),0_30px_90px_-40px_rgba(0,0,0,0.8)] backdrop-blur-2xl' : 'border-[#E7E0D4] bg-white shadow-[0_1px_2px_rgba(43,33,26,0.04),0_30px_80px_-48px_rgba(43,33,26,0.4)]'}`} data-id="element-6931" data-mp="30kgvamq8s2ay">
      {/* Horizontal scroll is only needed on narrow screens, and it clips the
       hover panel — so above md the table simply lays out at full width. */}
      <div className="overflow-x-auto md:overflow-visible" data-id="element-6932" data-mp="1k6qov9bnh1y5">
        <table className="w-full min-w-[560px] border-collapse text-left" data-id="element-6933" data-mp="1p5thfuag1fp8">
          <thead data-id="element-6934" data-mp="6b8j7lkzeedh">
            <tr className={`border-b ${dark ? 'border-white/12' : 'border-[#E7E0D4]'}`} data-id="element-6935" data-mp="6bqof7pmg2xj">
              <th scope="col" className="w-[48%] px-5 py-4 md:px-6" data-id="element-6936" data-mp="29wpp4yzl900">
                <span className="sr-only" data-id="element-6937" data-mp="27iw70jyg4uk">Feature</span>
              </th>
              <th scope="col" className="w-[17%] px-4 py-4 text-center align-bottom" data-id="element-6938" data-mp="3ten03mkszayr">
                <ColumnHead slug="chatgpt" name="ChatGPT" dark={dark} data-id="element-6939" data-mp="3ofsb22w8sq67" />
              </th>
              <th scope="col" className="w-[17%] px-4 py-4 text-center align-bottom" data-id="element-6940" data-mp="3ji0g100awnxs">
                <ColumnHead slug="claude" name="Claude" dark={dark} data-id="element-6941" data-mp="3eqifgd26wpdc" />
              </th>
              {/* The Giga column is the answer the table exists to give, so it
               is stated as a column rather than left level with the other two.
               The orange cap lifts it into a featured column. */}
              <th scope="col" className={`w-[18%] rounded-tr-[19px] border-x px-4 py-4 text-center align-bottom ${dark ? 'border-[#EA580C]/25 bg-[#EA580C]/[0.10]' : 'border-[#F3DECB] bg-[#FFF8F3]'}`} data-id="element-6942" data-mp="30q7ozb4slcsp">
                <ColumnHead slug="giga" name="Giga" featured dark={dark} data-id="element-6943" data-mp="25mtcultkkh0u" />
              </th>
            </tr>
          </thead>
          <tbody data-id="element-6944" data-mp="20oi2m9zd44i3">
            {CONNECTED_APPS_ROWS.map(row => <tr key={row.feature} className={`group border-b last:border-b-0 transition-colors duration-150 ease-out ${dark ? 'border-white/[0.08] hover:bg-white/[0.04]' : 'border-[#F1ECE3] hover:bg-[#FDFBF8]'}`} data-id="element-6945" data-mp="2ep2mzakxe8dp">
                {/* The description is the detail, not the point — it appears
                 over the row on hover so ten rows stay scannable, and it
                 overlays rather than expands so nothing below it moves. */}
                <th scope="row" className={`relative px-4 py-3.5 text-[13.5px] font-semibold leading-snug md:px-6 md:py-4 md:text-[14px] ${dark ? 'text-white' : 'text-[#2B211A]'}`} data-id="element-6946" data-mp="1vr9l8bm1g4hw">
                  <span className="block" data-id="element-6947" data-mp="1382v2g2hpvk7">{row.feature}</span>
                  {/* There is no hover on a phone, so the detail is simply
                   printed under the label below md. The overlay treatment
                   only exists to keep ten rows scannable on a pointer
                   device — where it cannot be revealed, it must be shown. */}
                  <span className={`mt-1 block text-[12px] font-normal leading-[1.45] md:hidden ${dark ? 'text-white/55' : 'text-[#8C7C6E]'}`} data-id="element-6948" data-mp="30x81ot43t5zo">
                    {row.description}
                  </span>
                  <span className={`pointer-events-none absolute left-4 right-2 top-[calc(100%-6px)] z-20 hidden max-w-xl translate-y-1 rounded-xl border px-3 py-2 text-[12.5px] font-normal leading-[1.45] opacity-0 transition-[opacity,transform] duration-150 ease-out group-hover:translate-y-0 group-hover:opacity-100 group-focus-within:translate-y-0 group-focus-within:opacity-100 md:left-6 md:block ${dark ? 'border-white/12 bg-[#221913] text-white/70 shadow-[0_18px_40px_-20px_rgba(0,0,0,0.8)]' : 'border-[#EFEAE1] bg-white text-[#6B5D52] shadow-[0_18px_40px_-20px_rgba(43,33,26,0.35)]'}`} data-id="element-6949" data-mp="3tl3x7uhxsruv">
                    {row.description}
                  </span>
                </th>
                <td className="px-2.5 py-3.5 text-center md:px-4 md:py-4" data-id="element-6950" data-mp="1p3nucmxqze9r">
                  <ComparisonValue value={row.chatgpt} dark={dark} data-id="element-6951" data-mp="1p5s8ryt9vvoa" />
                </td>
                <td className="px-2.5 py-3.5 text-center md:px-4 md:py-4" data-id="element-6952" data-mp="1kb4hjs6vwn60">
                  <ComparisonValue value={row.anthropic} dark={dark} data-id="element-6953" data-mp="1fctafp7z4qrh" />
                </td>
                <td className={`border-x px-2.5 py-3.5 text-center group-last:rounded-br-[19px] group-last:border-b-0 md:px-4 md:py-4 ${dark ? 'border-[#EA580C]/25 bg-[#EA580C]/[0.10]' : 'border-[#F3DECB] bg-[#FFF8F3]'}`} data-id="element-6954" data-mp="1af1e8j0aqihh">
                  <ComparisonValue value={row.giga} highlight dark={dark} data-id="element-6955" data-mp="11ems5hficzcx" />
                </td>
              </tr>)}
          </tbody>
        </table>
      </div>
    </div>;
}
/** Each column is named by its product, so the mark belongs with the name. */
function ColumnHead({
  slug,
  name,
  featured = false,
  dark = false
}: {
  slug: string;
  name: string;
  featured?: boolean;
  dark?: boolean;
}) {
  // Resolved through BrandLogo so a mark that disappears from the icon CDN
  // degrades to the brand's favicon instead of leaving a broken image — and so
  // ChatGPT's knot picks up the same optical scaling it gets everywhere else.
  // On dark ground the marks are still full-colour brand logos, so the chip
  // stays light — a dark chip leaves each logo floating with no plate.
  return <span className={`inline-flex items-center gap-2 rounded-full border py-1.5 pl-2.5 pr-3.5 ${dark ? featured ? 'border-[#EA580C]/45 bg-white' : 'border-white/15 bg-white/90' : featured ? 'border-[#EA580C]/30 bg-white' : 'border-[#E7E0D4] bg-[#FCFAF6]'}`} data-id="element-6956" data-mp="2fezmpy6db5c8">
      <BrandLogo slug={slug} className="size-[16px] shrink-0" data-id="element-6957" data-mp="2td7e7qkmxeg0" />
      <span className={`font-title text-[13.5px] font-bold leading-none md:text-[14.5px] ${featured ? 'text-[#2B211A]' : dark ? 'text-[#4A3B31]' : 'text-[#6B5D52]'}`} data-id="element-6958" data-mp="2tbctodd9zocg">
        {name}
      </span>
    </span>;
}
function ComparisonValue({
  value,
  highlight = false,
  dark = false
}: {
  value: ConnectedAppsValue;
  highlight?: boolean;
  dark?: boolean;
}) {
  // A bare glyph reads as a wireframe placeholder. Giving support and absence
  // their own tokens makes the column scannable as a shape, not a symbol.
  // One colour for "supported", in every column: a tick has to mean the same
  // thing wherever it appears, so the Giga column is featured by its tinted
  // fill and orange borders rather than by recolouring the semantics. The
  // brand orange rather than a green success token — this is a marketing
  // comparison, not a system state, so it should read as the brand's own mark.
  if (value === true) {
    return <span className={`inline-flex size-[22px] items-center justify-center rounded-full ring-1 ${dark ? 'bg-[#EA580C]/20 text-[#FDBA74] ring-[#FDBA74]/25' : 'bg-[#EA580C]/10 text-[#EA580C] ring-[#EA580C]/20'}`} aria-label="Supported" data-id="element-6959" data-mp="uvxbv5uec1pj">
        <Check size={13} strokeWidth={3.2} aria-hidden="true" data-id="element-6960" data-mp="14v4m3k5u3g8f" />
      </span>;
  }
  if (value === false) {
    return <span className={`inline-flex size-[22px] items-center justify-center rounded-full border ${dark ? 'border-white/15 text-white/30' : 'border-[#EFEAE1] text-[#CDC3B4]'}`} aria-label="Not supported" data-id="element-6961" data-mp="1nin6s7m58u40">
        <X size={12} strokeWidth={2.8} aria-hidden="true" data-id="element-6962" data-mp="1eh5vd2jolgln" />
      </span>;
  }
  if (value === null) {
    return <span aria-label="Not available" className={`inline-flex size-[22px] items-center justify-center text-[15px] leading-none ${dark ? 'text-white/25' : 'text-[#D8CFC2]'}`} data-id="element-6963" data-mp="1jepb66w121fp">
        –
      </span>;
  }
  return <span className={`inline-block rounded-md border px-1.5 py-1 text-[11px] font-semibold leading-tight ${dark ? highlight ? 'border-[#EA580C]/35 bg-[#EA580C]/15 text-[#FDBA74]' : 'border-white/12 bg-white/[0.06] text-white/65' : highlight ? 'border-[#EA580C]/25 bg-white text-[#EA580C]' : 'border-[#EFEAE1] bg-[#FCFAF6] text-[#6B5D52]'}`} data-id="element-6964" data-mp="19kfhmlvcmx3e">
      {value}
    </span>;
}
function IconBullet({
  icon: Icon,
  label
}: {
  icon: React.ElementType;
  label: string;
}) {
  return <p className="flex items-center gap-3" data-id="element-6965" data-mp="2shttt4sc0zm7">
      <span className="flex size-6 shrink-0 items-center justify-center text-[#EA580C]" data-id="element-6966" data-mp="2nqbx4sxcdl71">
        <Icon size={22} strokeWidth={2.4} data-id="element-6967" data-mp="2noh7x2f1f6x5" />
      </span>
      {label}
    </p>;
}